import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const BlogList = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);

  useEffect(() => {
    const fetchBlogIndex = async () => {
      try {
        const response = await fetch('/blog/blogIndex.json');
        if (!response.ok) {
          throw new Error('Failed to load blog posts');
        }
        const data = await response.json();
        const sortedPosts = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setBlogPosts(sortedPosts);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogIndex();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F5FAFF" }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 mb-4" style={{ border: "3px solid #EAF3FF", borderTopColor: "#0065F8" }}></div>
          <p className="text-sm" style={{ color: "#334155" }}>Loading articles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-red-600 text-lg">{error}</div>
      </div>
    );
  }

  // Main topic categories (simplified filtering)
  const mainCategories = [
    { id: 'all', label: 'All Topics' },
    { id: 'Screen Time', label: 'Screen Time' },
    { id: 'Digital Safety', label: 'Digital Safety' },
    { id: 'Educational Apps', label: 'Learning Apps' },
    { id: 'Interactive Stories', label: 'Storytelling' },
    { id: 'Child Development', label: 'Development' }
  ];

  let filteredPosts = selectedTag === 'all'
    ? blogPosts
    : blogPosts.filter(post => post.tags?.includes(selectedTag));

  if (searchQuery) {
    filteredPosts = filteredPosts.filter(post =>
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  const getReadingTime = (post) => {
    // Use readingTime field if available, otherwise calculate from description as fallback
    if (post.readingTime) {
      return post.readingTime;
    }
    const wordsPerMinute = 200;
    const words = post.description.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5FAFF", fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)", borderBottom: "2px solid rgba(11,26,51,0.06)" }}>
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full blur-3xl" style={{ background: "rgba(0,101,248,0.16)" }}></div>
        <div className="absolute bottom-0 left-20 w-96 h-96 rounded-full blur-3xl" style={{ background: "rgba(0,202,255,0.12)" }}></div>

        {/* Diagonal illustration on right */}
        <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[52%]" style={{
          clipPath: 'polygon(20% 100%, 45% 0%, 100% 0%, 100% 100%)'
        }}>
          <img
            src="/blog/images/header_illustration.png"
            alt="Creative child reading"
            className="absolute right-0 top-0 w-[115%] h-full object-cover"
            style={{
              objectPosition: 'right center'
            }}
          />
        </div>

        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          {/* clears the floating site navbar */}
          <div className="pt-24 sm:pt-28" />

          <div className="max-w-2xl pb-16 md:pb-24">
            <div
              className="inline-block mb-5"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: "#FFFFFF",
                border: "2px solid rgba(0,101,248,0.18)",
                color: "#0065F8",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                padding: "6px 14px",
                borderRadius: 999,
                textTransform: "uppercase",
              }}
            >
              Pixley Blog
            </div>

            <h1
              className="mb-5"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
                letterSpacing: "-0.02em",
                lineHeight: 1.05,
              }}
            >
              Resources and insights for <span style={{ color: "#0065F8" }}>creative parents</span>
            </h1>

            <p style={{ color: "#334155", fontSize: "clamp(1rem, 1.6vw, 1.2rem)", lineHeight: 1.6 }}>
              Practical advice on storytelling, child development, and creative parenting.
            </p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Article count */}
        <div className="mb-4">
          <span className="text-sm text-gray-500">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
          </span>
        </div>

        {/* Filter and Search Bar */}
        <div className="mb-12 flex items-center justify-between gap-6">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            {mainCategories.map((category) => {
              const active = selectedTag === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedTag(category.id)}
                  className="text-sm font-bold transition-all duration-150"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    padding: "8px 16px",
                    borderRadius: 999,
                    background: active ? "#0065F8" : "#FFFFFF",
                    color: active ? "#FFFFFF" : "#334155",
                    border: active ? "none" : "2px solid rgba(11,26,51,0.08)",
                    boxShadow: active ? "0px 3px 0px #073B8E" : "0px 2px 0px rgba(11,26,51,0.05)",
                    letterSpacing: "0.01em",
                  }}
                >
                  {category.label}
                </button>
              );
            })}
          </div>

          {/* Search - expandable */}
          <div className="flex items-center gap-3">
            {searchExpanded ? (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => {
                    if (!searchQuery) setSearchExpanded(false);
                  }}
                  autoFocus
                  className="w-64 pr-10 transition-all text-sm focus:outline-none"
                  style={{
                    padding: "10px 16px",
                    background: "#FFFFFF",
                    border: "2px solid rgba(0,101,248,0.18)",
                    borderRadius: 999,
                    color: "#0B1A33",
                  }}
                />
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4" fill="none" stroke="#0065F8" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            ) : (
              <button
                onClick={() => setSearchExpanded(true)}
                className="transition-colors"
                aria-label="Search articles"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  background: "#FFFFFF",
                  border: "2px solid rgba(11,26,51,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                }}
              >
                <svg className="h-4 w-4" fill="none" stroke="#0065F8" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Article Grid */}
        {filteredPosts.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, index) => {
              // On-brand cream-blue tonal fallback patterns
              const patterns = [
                { icon: '📚' },
                { icon: '✨' },
                { icon: '🎯' },
                { icon: '💡' },
                { icon: '🌟' },
                { icon: '🎨' },
              ];
              const pattern = patterns[index % patterns.length];

              return (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block"
                >
                  <article
                    className="overflow-hidden transition-all duration-200"
                    style={{
                      background: "#FFFFFF",
                      border: "2px solid rgba(11,26,51,0.06)",
                      borderRadius: 20,
                      boxShadow: "0px 3px 0px rgba(11,26,51,0.06), 0px 10px 24px rgba(11,26,51,0.06)",
                    }}
                  >
                    {/* Visual Asset Area */}
                    {post.coverImage ? (
                      <div className="h-36 overflow-hidden">
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="h-36 flex items-center justify-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, #EAF3FF 0%, #DCEEFF 100%)" }}>
                        <div className="text-6xl opacity-50 group-hover:scale-110 transition-transform duration-300">
                          {pattern.icon}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 text-xs" style={{ color: "#64748B" }}>
                        <time>
                          {/* +T00:00:00 forces local-time parsing — a bare
                              "YYYY-MM-DD" parses as UTC midnight and renders a
                              day early for anyone west of UTC */}
                          {new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </time>
                        <span>·</span>
                        <span>{getReadingTime(post)} min read</span>
                      </div>

                      <h3
                        className="mb-2 leading-tight line-clamp-2 transition-colors"
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          color: "#0B1A33",
                          fontWeight: 700,
                          fontSize: 18,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {post.title}
                      </h3>

                      <p className="mb-3 leading-relaxed line-clamp-2" style={{ color: "#334155", fontSize: 13.5 }}>
                        {post.description}
                      </p>

                      {/* Author and Tags */}
                      <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid rgba(11,26,51,0.06)" }}>
                        <div className="flex items-center gap-2">
                          <img
                            src="/assets/pixley-panda-nobg.png"
                            alt="Pixley"
                            className="w-5 h-5 rounded-full object-cover"
                          />
                          <span className="text-xs font-semibold" style={{ color: "#334155" }}>{post.author}</span>
                        </div>

                        {post.tags && post.tags.length > 0 && (
                          <div className="flex gap-1.5">
                            {post.tags.slice(0, 1).map((tag) => (
                              <span
                                key={tag}
                                style={{
                                  fontSize: 11,
                                  color: "#0065F8",
                                  background: "#EAF3FF",
                                  border: "1.5px solid rgba(0,101,248,0.18)",
                                  padding: "3px 9px",
                                  borderRadius: 999,
                                  fontWeight: 700,
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}

        {filteredPosts.length === 0 && (
          <div
            className="text-center py-20"
            style={{
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.08)",
              borderRadius: 20,
              boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
            }}
          >
            <h3 style={{ fontFamily: "'Fredoka', sans-serif", color: "#0B1A33", fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
              No articles found
            </h3>
            <p className="mb-6" style={{ color: "#334155" }}>Try adjusting your search or filters.</p>
            <button
              onClick={() => {
                setSelectedTag('all');
                setSearchQuery('');
              }}
              className="transition-colors"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: 14,
                padding: "12px 24px",
                borderRadius: 999,
                border: "none",
                boxShadow: "0px 4px 0px #073B8E",
                cursor: "pointer",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
